import { useEffect, useState, useCallback } from 'react'
import { onApiError } from '@/services/api'
import { FiX, FiAlertCircle, FiWifiOff } from 'react-icons/fi'

interface Toast {
  id: number
  message: string
  type: 'error' | 'warning'
}

let nextId = 0

const ApiErrorToast = () => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  useEffect(() => {
    return onApiError((message) => {
      const id = ++nextId
      const isNetwork = message.includes('Cannot reach') || message.includes('network') || message.includes('timed out')
      setToasts((prev) => {
        if (prev.some((t) => t.message === message)) return prev
        return [...prev.slice(-4), { id, message, type: isNetwork ? 'warning' : 'error' }]
      })
      setTimeout(() => dismiss(id), 8000)
    })
  }, [dismiss])

  if (toasts.length === 0) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: '100px',
        right: '16px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '420px',
        width: '100%',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => {
        const isWarning = toast.type === 'warning'
        return (
          <div
            key={toast.id}
            style={{
              pointerEvents: 'auto',
              fontFamily: 'Lexend Deca',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px 16px',
              borderRadius: '10px',
              backgroundColor: isWarning ? '#FEF9EB' : '#FEE2E2',
              border: `1px solid ${isWarning ? '#EEC233' : '#E93323'}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              animation: 'slideInRight 0.25s ease-out',
            }}
          >
            <div style={{ flexShrink: 0, marginTop: '2px' }}>
              {isWarning ? (
                <FiWifiOff style={{ width: '18px', height: '18px', color: '#EEC233' }} />
              ) : (
                <FiAlertCircle style={{ width: '18px', height: '18px', color: '#E93323' }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '13px', lineHeight: '18px', color: isWarning ? '#92740C' : '#991B1B', marginBottom: '2px' }}>
                {isWarning ? 'Connection Error' : 'Something went wrong'}
              </div>
              <div style={{ fontSize: '12px', lineHeight: '18px', color: isWarning ? '#92740C' : '#991B1B', opacity: 0.85 }}>
                {toast.message}
              </div>
            </div>
            <button
              onClick={() => dismiss(toast.id)}
              style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: isWarning ? '#92740C' : '#991B1B', opacity: 0.6 }}
            >
              <FiX style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
        )
      })}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

export default ApiErrorToast
